import { type ReactNode, useEffect } from 'react'
import { Outlet } from 'react-router'
import { ErrorBox } from '@/components/commons/error-box'
import { AlertTriangleIcon } from '@/components/ui/icons'
import { ErrorBoundary } from '@/error-boundary'
import { useGetPhotosetImagesList } from '@/features/photoset/queries/use-get-photoset-images-list'
import { usePhotosetStore } from '@/features/photoset/store/store'
import { type Photoset, PhotosetURLParams } from '@/features/photoset/types'
import { useURLParsedParams } from '@/hooks/use-url-parsed-params'
import { useTranslation } from '@/translations/i18n'

export const PhotosetRoute = () => {
	const { t } = useTranslation()
	const urlParams = useURLParsedParams(PhotosetURLParams)

	return urlParams.success ? (
		<PhotosetRouteDataInitializer photosetId={urlParams.data.id}>
			<ErrorBoundary>
				<Outlet context={{ urlParams: urlParams.data }} />
			</ErrorBoundary>
		</PhotosetRouteDataInitializer>
	) : (
		<ErrorBox
			icon={<AlertTriangleIcon />}
			title={t('errors:invalid_url')}
		/>
	)
}

const PhotosetRouteDataInitializer = ({
	photosetId,
	children
}: {
	photosetId: Photoset['id']
	children: ReactNode
}) => {
	const imagesQuery = useGetPhotosetImagesList({ urlParams: { photosetId } })
	const initImages = usePhotosetStore((state) => state.initImages)

	useEffect(() => {
		initImages(imagesQuery.data ?? [])
	}, [imagesQuery.data, initImages])

	return children
}
